import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShield, FiX } from 'react-icons/fi';
import {
    clearKnownOptionalCookies,
    getCookieConsentStatus,
    setCookieConsentStatus,
} from '@/utils/cookieConsent';

const CookieConsentBanner: React.FC = () => {
    const [consent, setConsent] = useState(getCookieConsentStatus());
    const [visible, setVisible] = useState(true);

    const handleAccept = () => {
        setCookieConsentStatus('accepted');
        setVisible(false);
        setTimeout(() => setConsent('accepted'), 400);
    };

    const handleDecline = () => {
        setCookieConsentStatus('declined');
        clearKnownOptionalCookies();
        setVisible(false);
        setTimeout(() => setConsent('declined'), 400);
    };

    const shouldRender = consent === 'unset';

    return (
        <AnimatePresence>
            {shouldRender && visible && (
                <motion.div
                    key="cookie-banner"
                    initial={{ y: 120, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 120, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 280, damping: 30 }}
                    className="fixed bottom-5 left-4 right-4 z-[200] pointer-events-none"
                >
                    <div className="pointer-events-auto mx-auto max-w-3xl">
                        {/* Card */}
                        <div className="relative overflow-hidden rounded-2xl border border-neutral-200/80 bg-white shadow-2xl dark:border-gray-700/60 dark:bg-gray-900">
                            {/* Top accent line */}
                            <div className="h-0.5 w-full bg-gradient-to-r from-primary-500 via-primary-400 to-primary-600" />

                            <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:gap-6 sm:p-6">

                                {/* ── Header row: icon + title + close ── */}
                                <div className="flex items-center gap-3 sm:contents">
                                    {/* Icon */}
                                    <div className="flex-shrink-0">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 ring-1 ring-primary-100 dark:bg-primary-900/30 dark:ring-primary-800/50">
                                            <FiShield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <p className="flex-1 text-sm font-semibold text-neutral-900 dark:text-gray-100 sm:hidden">
                                        We value your privacy
                                    </p>

                                    {/* Close — mobile only, stays in the header row */}
                                    <button
                                        type="button"
                                        onClick={handleDecline}
                                        aria-label="Close cookie notice"
                                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-gray-800 dark:hover:text-gray-300 sm:hidden"
                                    >
                                        <FiX className="h-4 w-4" />
                                    </button>
                                </div>

                                {/* Text block */}
                                <div className="min-w-0 flex-1">
                                    {/* Title — desktop only */}
                                    <p className="hidden text-sm font-semibold text-neutral-900 dark:text-gray-100 sm:block">
                                        We value your privacy
                                    </p>
                                    <p className="text-sm leading-relaxed text-neutral-500 dark:text-gray-400 sm:mt-1">
                                        SideGO uses essential cookies to keep the platform secure and
                                        functional. With your permission, we also use optional analytics
                                        cookies to improve your experience.{' '}
                                        <Link
                                            to="/cookies"
                                            className="font-medium text-primary-600 underline-offset-2 hover:underline dark:text-primary-400"
                                        >
                                            Cookie Policy
                                        </Link>
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex flex-shrink-0 items-center gap-2 sm:flex-col sm:items-stretch lg:flex-row">
                                    <button
                                        type="button"
                                        onClick={handleDecline}
                                        className="flex-1 rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-300 hover:bg-neutral-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700 sm:flex-none"
                                    >
                                        Decline
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleAccept}
                                        className="flex-1 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 active:bg-primary-800 sm:flex-none"
                                    >
                                        Accept All
                                    </button>
                                </div>

                                {/* Close — desktop only, sits at top-right */}
                                <button
                                    type="button"
                                    onClick={handleDecline}
                                    aria-label="Close cookie notice"
                                    className="absolute right-3 top-3 hidden h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 dark:hover:bg-gray-800 dark:hover:text-gray-300 sm:flex"
                                >
                                    <FiX className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default CookieConsentBanner;
